
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { List, FileText } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { ActivePlan } from '@/hooks/license/useResellerPlanManagement';

interface ActivePlansCardProps {
  plans?: ActivePlan[];
  title?: string;
  description?: string;
  onToggleEnabled?: (planId: string, enabled: boolean) => void;
  // For backward compatibility
  activePlans?: ActivePlan[];
  currentPlan?: ActivePlan;
}

const ActivePlansCard: React.FC<ActivePlansCardProps> = ({ 
  plans,
  activePlans,
  currentPlan,
  title = "Active Plans",
  description = "View and manage your active reseller plans",
  onToggleEnabled
}) => {
  // Use the plans prop if provided, otherwise fall back to activePlans or wrap currentPlan in an array
  const displayPlans = plans || activePlans || (currentPlan ? [currentPlan] : []);

  const handleToggleEnabled = (planId: string, enabled: boolean) => {
    if (onToggleEnabled) {
      onToggleEnabled(planId, enabled);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <List className="mr-2 h-5 w-5" /> {title}
        </CardTitle>
        {description && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {displayPlans.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              No active plans found
            </div>
          ) : (
            displayPlans.map((plan) => (
              <div key={plan.id} className="border rounded-md p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{plan.name}</div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Enabled</span>
                    <Switch
                      checked={plan.enabled ?? true}
                      onCheckedChange={(checked) => handleToggleEnabled(plan.id, checked)}
                    />
                    <div className="text-sm px-2 py-1 bg-green-100 text-green-800 rounded-full">
                      {plan.status}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  <div className="flex justify-between">
                    <span>Super Admins:</span>
                    <span className="font-medium">{plan.admins}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Users:</span>
                    <span className="font-medium">{plan.users}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="mt-4 text-center">
          <Button variant="ghost" className="text-sm">
            <FileText className="mr-2 h-4 w-4" /> View All Plans
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivePlansCard;
